import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  MouseEvent,
  FormEvent,
} from "react";
import { useSelector } from "react-redux";
import _map from "lodash/map";
import classNames from "classnames";

import { useAppSelector } from "@web-solutions/store/types";
import { PaymentSystem } from "@web-solutions/react-billing";

import Analytics from "@web-solutions/module-analytics";
import { TM, t, tm } from "@web-solutions/module-localization";
import { useRemoteConfig } from "@web-solutions/base-app/hooks/use-remote-config";
import { LocalizedString } from "@web-solutions/core/constants/remote-config";

import { remoteConfigSelector } from "core/store/remote-config/selectors";
import { selectPaymentSystem } from "core/store/billing/selectors";
import {
  InjectedPaymentMethod,
  PaymentModalType,
} from "core/constants/remote-config";
import { CardFormLayout } from "core/payment/constants";
import { ProductInfo } from "core/payment/base/product-info";
import PaymentForm from "core/payment/components/payment-form";
import {
  BillingConsent,
  BillingTermsLegal,
} from "core/payment/components/billing-consent";
import TrialPrice from "core/payment/base/product-info/components/trial-price";
import ApplePay from "core/payment/components/apple-pay";
import GooglePay from "core/payment/components/google-pay";
import { enrichStripeIntroProduct } from "core/utils/stripe-payment";
import { PayPalBtn } from "core/payment/components/paypal";
import { MercadoPagoBtn } from "core/payment/components/mercado-pago";
import TrialReminder from "core/payment/components/trial-reminder";
import { Title } from "core/ui-elements";

import { Options } from "core/payment";
import { type Subscription } from "core/interfaces/billing";

import InjectedPaymentMethodHeader from "core/summary/injected-payment-method/components/header";
import InjectedPaymentMethodSwitch from "core/summary/injected-payment-method/components/switch";

import { PaymentSafe } from "core/containers/payment-safe";

import {
  ProductDetails,
  selectInjectedPaymentMethod,
} from "core/store/billing/selectors";
import type { PaymentModalTitle } from "core/interfaces/payment-modal";

import PaymentDescription from "../../components/payment-description";
import { TotalPrice } from "../product-info/components/simple-view";
import { getActualPrices } from "../product-info/components/simple-view/utilts";

import { ICONS } from "./icons/cards";
import { ReactComponent as IconArrowDown } from "./icons/icon-arrow-down.svg";
import { ReactComponent as IconCreditCard } from "./icons/credit-card.svg";

import classes from "./style.module.scss";
import { useStore } from "../../../../../apps/virtual-brainary/src/onboarding/store/store";
import getLocale from "core/localization/get-locale";

const tKey = "core.payment_popup";

interface Props {
  activeProduct: ProductDetails;
  /** The plan card the user picked — see BasePaymentModal. */
  selectedProduct?: ProductDetails;
  onSubmit: (formData: any) => Promise<void>;
  onSuccess: (purchase: Subscription) => void;
  onError: (error: any) => void;
  options: Options;
  showApplePay: boolean;
  showPayPal: boolean;
  showGooglePay: boolean;
  onApplePayClick: () => void;
  onApplePaySubmit: (formData: any) => Promise<void>;
  onGooglePayClick: () => void;
  onGooglePaySubmit: (formData: any) => Promise<void>;
  onPayPalSubmit: (formData: any) => Promise<void>;
  onPayPalSuccess: (p: Subscription) => void;
  onPayPalError: (e: any) => void;
  onUserInfoChange: (info: any) => void;
  isOnlyBaseModal?: boolean;
  title?: PaymentModalTitle;
  subtitle?: React.ReactNode;
  showCard?: boolean;
  showMercadoPago: boolean;
  upsaleTrigger?: string;
  onSaveTokenOneTime?: (tokenId: string) => void;
  showModalPopup?: boolean
  /** Modal visibility — the billing-terms consent resets on every open. */
  isOpen?: boolean;
}

function addDaysToDate(days: string, locale: string): string {
  const daysNumber = parseInt(days, 10);
  if (isNaN(daysNumber)) {
    return '';
  }

  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + daysNumber);

  // In the funnel's language: "5. Oktober 2026" (de), "5 octobre 2026" (fr).
  const formatter = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const formattedDate = formatter.format(currentDate);
  console.log('formattedDate ', formattedDate);
  return formattedDate;
}

const BasePaymentModalBody: React.FC<Props> = ({
  activeProduct: rawActiveProduct,
  selectedProduct,
  onSubmit,
  onSuccess,
  onError,
  isOnlyBaseModal,
  options,
  showApplePay,
  showPayPal,
  showGooglePay,
  onApplePayClick,
  onApplePaySubmit,
  onGooglePayClick,
  onGooglePaySubmit,
  onPayPalSubmit,
  onPayPalSuccess,
  onPayPalError,
  onUserInfoChange,
  title,
  subtitle,
  showCard = true,
  showMercadoPago,
  upsaleTrigger,
  onSaveTokenOneTime,
  showModalPopup,
  isOpen,
}) => {
  const paymentSystem = useSelector(selectPaymentSystem);

  // Defensive enrichment: if upstream missed the Stripe-intro override
  // (different rendering paths for Apple Pay vs card-only modal can produce
  // an unenriched product), apply it here so all downstream consumers
  // (ProductInfo, ApplePay, etc.) see today=$0.99 + discount values.
  const isStripeFlow =
    paymentSystem === PaymentSystem.STRIPE || Boolean(rawActiveProduct?.stripeId);
  const introAmount = rawActiveProduct?.trialPriceAmount ? Number(rawActiveProduct.trialPriceAmount) : (rawActiveProduct?.amount ? Number(rawActiveProduct.amount) : (rawActiveProduct?.priceAmount ? Number(rawActiveProduct.priceAmount) : undefined));
  const activeProduct = isStripeFlow && !rawActiveProduct?.isTrial
    ? (enrichStripeIntroProduct(rawActiveProduct, introAmount) || rawActiveProduct)
    : rawActiveProduct;
  const {
    isShowSafeCheckout,
    showTermsAtPlans,
    cardFormCollapsed,
    cardFormLayout,
    isInjectedPaymentMethodOnPage,
    paymentModalType,
    isTrialPeriodReminder,
    simpleTitleModal,
    totalPriceType,
  } = useSelector(remoteConfigSelector);
  const hiddenAltMethods =
    isInjectedPaymentMethodOnPage && !activeProduct?.isOneTimePurchase;
  const [showCardForm, setShowCardForm] = useState(
    // Stripe's Google Pay deliberately does not count here. Whether that button
    // shows up is only known a moment later (see below), while this decision is
    // taken once, on mount — counting it would collapse the card form behind a
    // toggle for a button that may never appear, and on a funnel with no other
    // wallet that leaves the modal with no way to pay.
    !cardFormCollapsed
    || (!showApplePay && !showPayPal && !(showGooglePay && !isStripeFlow))
  );

  // Stripe's Google Pay button answers this itself — the wallet needs Chrome,
  // HTTPS and a card in the Google account, none of which the remote-config flag
  // knows about. Until it says yes, no slot and no "or" are drawn for a button
  // that never appears. Other systems keep going by the flag alone: their SDK
  // renders into the container below and reports nothing back.
  const [isStripeGooglePayReady, setIsStripeGooglePayReady] = useState(false);
  const showGooglePayMethod =
    showGooglePay && (!isStripeFlow || isStripeGooglePayReady);

  // Same for Apple Pay on Stripe: `showApplePay` only says the browser has
  // Apple Pay, the button itself answers through `canMakePayment` whether the
  // sheet can open here. Until it says yes, no slot and no "or" above the card
  // form — otherwise the modal showed an "or" with the card as the only method.
  const [isStripeApplePayReady, setIsStripeApplePayReady] = useState(false);
  const showApplePayMethod =
    showApplePay && (!isStripeFlow || isStripeApplePayReady);

  const state = useStore((state) => state);
  const { setPage, showDownSale } = state;

  const injectedPaymentMethod = useSelector(selectInjectedPaymentMethod);

  const injectedApplePay =
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_GOOGLEPAY ||
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY ||
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_PAYPAL;
  const injectedPayPal =
    injectedPaymentMethod === InjectedPaymentMethod.PAYPAL ||
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_PAYPAL ||
    injectedPaymentMethod === InjectedPaymentMethod.GOOGLEPAY_PAYPAL;
  const injectedGooglePay =
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_GOOGLEPAY ||
    injectedPaymentMethod === InjectedPaymentMethod.GOOGLEPAY ||
    injectedPaymentMethod === InjectedPaymentMethod.GOOGLEPAY_PAYPAL;
  const injectedCard = injectedPaymentMethod === InjectedPaymentMethod.CARD;
  const injectedApplePayOrGooglePay =
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_GOOGLEPAY;
  const injectedGooglePayPayPal =
    injectedPaymentMethod === InjectedPaymentMethod.GOOGLEPAY_PAYPAL;
  const injectedApplePayPayPal =
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_PAYPAL;

  const modalType = isOnlyBaseModal
    ? PaymentModalType.BASE
    : paymentModalType;

  const isBaseModal = modalType === PaymentModalType.BASE;
  const isTopModal = modalType === PaymentModalType.TOP_INJECTED;
  const isSimpleModal = modalType === PaymentModalType.SIMPLE;
  const isSimpleTitleModal = modalType === PaymentModalType.SIMPLE_TITLE;
  const isBottomModal = modalType === PaymentModalType.BOTTOM_INJECTED;

  const isSimpleView = isSimpleModal || isSimpleTitleModal;

  // const locale: keyof LocalizedString = useAppSelector(
  //   (state) => state.locale.locale
  // );
  const locale = getLocale();

  const {
    paymentPopup,
    newPaymentModal,
    specialOffer,
    products,
    showBillingTermsConsent,
    policyLinks,
  } = useRemoteConfig();

  // Billing-terms consent: unchecked on every open, blocks all payment methods
  // of this modal until the user ticks it.
  const [isConsentAccepted, setIsConsentAccepted] = useState(false);
  const [isConsentError, setIsConsentError] = useState(false);
  const consentRef = useRef<HTMLDivElement>(null);
  const isConsentMissing = Boolean(showBillingTermsConsent) && !isConsentAccepted;

  useEffect(() => {
    if (isOpen) {
      setIsConsentAccepted(false);
      setIsConsentError(false);
    }
  }, [isOpen]);

  const handleConsentChange = useCallback((accepted: boolean) => {
    setIsConsentAccepted(accepted);

    if (accepted) {
      setIsConsentError(false);
    }

    Analytics.trackEvent("billing_consent", accepted ? "checked" : "unchecked");
  }, []);

  const flagMissingConsent = useCallback(() => {
    setIsConsentError(true);
    Analytics.trackEvent("billing_consent", "blocked");
    consentRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, []);

  // Card form: the DOM submit is caught on the wrapper in the capture phase, so
  // the card is never even tokenized when the consent is missing.
  const handleCardFormSubmitCapture = (e: FormEvent) => {
    if (!isConsentMissing) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    flagMissingConsent();
  };

  // SDK-driven forms (Solidgate) submit without a DOM event — last guard before
  // anything is charged.
  const withConsent = useCallback(
    <T,>(handler: (payload: T) => Promise<void> | void) =>
      async (payload: T) => {
        if (isConsentMissing) {
          flagMissingConsent();
          return;
        }

        await handler(payload);
      },
    [isConsentMissing, flagMissingConsent]
  );

  const handleTermsClick = useCallback(() => setPage("terms-of-use"), [setPage]);

  // Billing terms live in the Terms of Use unless the config points at an
  // absolute URL — relative ones are rewritten to index.html by hosting and
  // would just restart the funnel.
  const handleBillingTermsClick = useCallback(() => {
    const url = policyLinks?.billingTerms;

    if (url?.startsWith("http")) {
      window.open(url, "_blank", "noopener");
      return;
    }

    setPage("terms-of-use");
  }, [policyLinks, setPage]);

  const handlePayWithCardClick = (e: MouseEvent) => {
    e.preventDefault();
    if (showApplePay || showPayPal || showGooglePayMethod) {
      setShowCardForm(!showCardForm);
    }
  };

  const formWrapStyle = classNames(classes.formWrap, {
    [classes.formWrapOpen]: showCardForm || injectedCard,
  });

  const expandWrapStyle = classNames(classes.expandWrap, {
    [classes.expand]: showCardForm || injectedCard,
  });

  const color = useMemo(() => {
    const root = document.querySelector(":root") as HTMLElement;

    return getComputedStyle(root).getPropertyValue(
      "--injectedPaymentPrimaryColor"
    );
  }, []);

  const cardFormProps = useMemo(
    () => ({
      isSimpleView,
      product: activeProduct,
      color,
      options,
      paymentSystem,
      formClassName: "payment-form",
      fieldClassName: classNames("form-field", {
        "form-field-compact": cardFormLayout === CardFormLayout.COMPACT,
      }),
      onSubmit: withConsent(onSubmit),
      onSuccess,
      onError,
      onApplePayClick,
      onApplePaySubmit: withConsent(onApplePaySubmit),
      onGooglePayClick,
      onGooglePaySubmit: withConsent(onGooglePaySubmit),
      onUserInfoChange,
      onSaveTokenOneTime,
    }),
    [
      isSimpleView,
      color,
      activeProduct,
      options,
      cardFormLayout,
      paymentSystem,
      onSubmit,
      onSuccess,
      onError,
      onApplePayClick,
      onApplePaySubmit,
      onGooglePayClick,
      onGooglePaySubmit,
      onUserInfoChange,
      onSaveTokenOneTime,
      withConsent,
    ]
  );

  const { fullPrice, actualPrice, vat, vatPrice } = getActualPrices({
    activeProduct,
    totalPriceType,
  });

  // The description follows the plan card the user actually picked. Two traps
  // here: it used to read `products[0]` from the config (so switching cards
  // never changed the text), and for Stripe `activeProduct` is the subscription
  // half of the pair — it carries the regular price but the OTHER card's title
  // and intro price, which is why the modal showed a different plan than the
  // page. `selectedProduct` is the card itself; the config products remain a
  // fallback for the first render, before anything is picked.
  // `{}` is a real value here — the paywall starts with an empty active product
  // before billing resolves — so pick the first candidate that carries data.
  const descriptionProduct = [
    selectedProduct,
    activeProduct,
    showDownSale ? specialOffer.products?.[0] : products?.[0],
  ].find(
    (product: any) =>
      product?.title || product?.priceStartTrial || product?.plan_end_days
  ) as any;

  const productDescription = useMemo(() => {
    // Plan products keep these under `priceStartTrial`, special-offer products
    // keep `plan_end_days` at the top level and may carry a plain string price —
    // both shapes reach this modal, so both are handled.
    const priceStartTrial = descriptionProduct?.priceStartTrial;
    const price = priceStartTrial?.price;

    return {
      name: descriptionProduct?.name ?? "",
      // hairstyle special-offer records carry neither plan_end_days nor
      // subTitle: fall back to trialDays and priceStartTrial.billedAmount
      plan_end_days: addDaysToDate(
        priceStartTrial?.plan_end_days ??
          descriptionProduct?.plan_end_days ??
          descriptionProduct?.trialDays,
        locale
      ),
      subTitle:
        descriptionProduct?.subTitle?.[locale] ??
        priceStartTrial?.billedAmount ??
        "",
      titleProduct: descriptionProduct?.title?.[locale] ?? "",
      priceProduct: (typeof price === "string" ? price : price?.[locale]) ?? "",
    };
  }, [descriptionProduct, locale]);

  const { name, plan_end_days, subTitle, titleProduct, priceProduct } = productDescription;

  return (
    <div
      className={classNames(classes.contentContainer, {
        [classes.simple]: isSimpleView,
      })}
    >
      {isSimpleTitleModal && simpleTitleModal?.titleEnabled && (
        <header
          className={classes.header}
          style={
            !simpleTitleModal?.withTotalPriceUnder
              ? { display: "contents" }
              : {}
          }
        >
          <Title
            className={classes.title}
            style={
              simpleTitleModal?.fontSize
                ? { fontSize: `${simpleTitleModal.fontSize}px` }
                : {}
            }
          >
            {title?.simple_title || (
              <TM k={t(`${tKey}.simple_type.title_default`)} />
            )}
          </Title>
          {simpleTitleModal?.withTotalPriceUnder && (
            <TotalPrice
              className={classes.totalPrice}
              mode={simpleTitleModal?.type}
              vat={vat}
              vatPrice={vatPrice}
              fullPrice={fullPrice}
              actualPrice={actualPrice}
            />
          )}
        </header>
      )}
      <div
        className={classNames(classes.wrap, {
          [classes.simple_title]: isSimpleTitleModal,
        })}
      >
        {(isBaseModal || isBottomModal) && (
          <>
            <ProductInfo
              product={activeProduct}
              subtitle={subtitle}
              modalType={modalType}
            />
            {isTrialPeriodReminder && !activeProduct.isOneTimePurchase && (
              <TrialReminder className={classes.reminder} />
            )}
          </>
        )}
        {(isTopModal || isBottomModal) && (
          <>
            <InjectedPaymentMethodHeader isCentralHeader={isTopModal} />
            <InjectedPaymentMethodSwitch isBaseSwitch />
          </>
        )}

        {isSimpleView && (
          <>
            <InjectedPaymentMethodSwitch isBaseSwitch />
            <ProductInfo
              product={activeProduct}
              subtitle={subtitle}
              isSimpleView={isSimpleView}
              modalType={modalType}
            />
            <div className={classes.divider} />
          </>
        )}

        <div
          className={classNames(
            classes.payment,
            isBottomModal ? classes.row : classes.column
          )}
        >
          {isBottomModal && !injectedCard && (
            <TrialPrice
              textLineThrough={tm(
                activeProduct.textLineThrough,
                "",
                activeProduct
              )}
              trialPrice={activeProduct.trialPrice}
              className={classes.price}
            />
          )}

          {isTopModal && (
            <>
              <div className={classes.space} />
              <ProductInfo product={activeProduct} modalType={modalType} />
              {isTrialPeriodReminder && !activeProduct.isOneTimePurchase && (
                <TrialReminder className={classes.reminder} />
              )}
            </>
          )}

          {/* Alt methods hand off to native sheets on click, so they are made
              inert while the consent is missing — the click then lands on this
              container and flags the checkbox instead. */}
          <div
            className={classNames(classes.paymentMethods, {
              [classes.hidden]: hiddenAltMethods,
              [classes.consentLocked]: isConsentMissing,
            })}
            onClick={isConsentMissing ? flagMissingConsent : undefined}
          >
            {/* Kept mounted on the flag alone — it is what reports back whether
                the wallet is there; the wrapper is what collapses when it is not. */}
            {((showApplePay && injectedApplePay) ||
              (showApplePay && isBaseModal)) && (
              <div
                // Inline margin would win over `.hidden`'s `margin: 0` and leave
                // a 16px gap where the button isn't.
                style={showApplePayMethod ? { marginTop: "16px" } : undefined}
                className={classNames(classes.buttonWrapper, {
                  [classes.hidden]: !showApplePayMethod,
                })}
              >
                <ApplePay
                  activeProduct={activeProduct}
                  className={classes.appleButton}
                  onApplePayClick={onApplePayClick}
                  onApplePaySubmit={withConsent(onApplePaySubmit)}
                  onApplePaySuccess={onSuccess}
                  onApplePayError={onError}
                  onAvailabilityChange={setIsStripeApplePayReady}
                />
              </div>
            )}
            {
              <div
                className={classNames(classes.buttonWrapper, {
                  [classes.hidden]:
                    !showApplePay || !(injectedApplePay || isBaseModal),
                })}
              >
                <div id={options?.applePayButtonParams?.containerId}></div>
              </div>
            }

            {showApplePayMethod &&
              ((isBaseModal && showCard) ||
                (injectedApplePayPayPal && showPayPal) ||
                (injectedApplePayOrGooglePay && showGooglePayMethod)) && (
                <div className={classes.or}>
                  <p>{paymentPopup.or[locale]}</p>
                </div>
              )}

            {/* Stripe's Google Pay button. Renders nothing for the systems whose
                SDK draws its own into the container below. Kept mounted on the
                flag alone — it is what reports back whether the wallet is there;
                the wrapper is what collapses when it is not. The submit goes
                through `withConsent` exactly like Apple Pay's: the sheet is
                native and cannot be told about the billing-terms checkbox. */}
            {showGooglePay && (injectedGooglePay || isBaseModal) && (
              <div
                className={classNames(classes.buttonWrapper, {
                  [classes.hidden]: !showGooglePayMethod,
                })}
              >
                <GooglePay
                  activeProduct={activeProduct}
                  className={classes.googleButton}
                  onGooglePayClick={onGooglePayClick}
                  onGooglePaySubmit={withConsent(onGooglePaySubmit)}
                  onAvailabilityChange={setIsStripeGooglePayReady}
                />
              </div>
            )}
            {
              <div
                className={classNames(classes.buttonWrapper, {
                  [classes.hidden]: !(injectedGooglePay || isBaseModal),
                })}
              >
                <div id={options?.googlePayButtonParams?.containerId}></div>
              </div>
            }

            {!injectedApplePayOrGooglePay &&
              showGooglePayMethod &&
              ((isBaseModal && showCard) || injectedGooglePayPayPal) && (
                <div className={classes.or}>
                  <p>{paymentPopup.or[locale]}</p>
                </div>
              )}

            {showPayPal && (injectedPayPal || isBaseModal) && (
              <>
                <PayPalBtn
                  product={activeProduct}
                  className={classes.paypal}
                  onSubmit={withConsent(onPayPalSubmit)}
                  onSuccess={onPayPalSuccess}
                  onError={onPayPalError}
                />
                {isBaseModal && showCard && (
                  <div className={classes.or}>
                    <p>{paymentPopup.or[locale]}</p>
                  </div>
                )}
              </>
            )}
            {showMercadoPago && (
              <>
                <MercadoPagoBtn
                  product={activeProduct}
                  upsaleTrigger={upsaleTrigger}
                />
                {isBaseModal && showCard && (
                  <div className={classes.or}>
                    <p>{paymentPopup.or[locale]}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div
          className={classNames(formWrapStyle, {
            [classes.hidden]: !showCard || !(injectedCard || isBaseModal),
            [classes.simple_view]: isSimpleView,
            [classes.consentLockedForm]: isConsentMissing,
          })}
          onSubmitCapture={handleCardFormSubmitCapture}
        >
          <button
            type="button"
            onClick={handlePayWithCardClick}
            className={classes.payWithCard}
          >
            <IconCreditCard
              width={30}
              height={21}
              className={classes.icon_card}
            />
            <p className={classes.payWithCardText}>
              {paymentPopup.payWithCard[locale]}
            </p>

            {/* delete arrow */}
            {(showApplePay || showPayPal || showGooglePayMethod) && !injectedCard && (
              <div className={classes.arrowDownWrap}>
                <IconArrowDown
                  width={12}
                  height={6}
                  className={classNames(classes.iconArrow, {
                    [classes.iconArrowUp]: showCardForm,
                  })}
                />
              </div>
            )}
          </button>
          <div className={expandWrapStyle}>
            <ul className={classes.cardsList}>
              {_map(ICONS, (Icon, key) => (
                <li key={key} className={classes.cardItem}>
                  <Icon width="100%" height="100%" />
                </li>
              ))}
            </ul>
            <PaymentForm {...cardFormProps} />
          </div>
        </div>

        {(showBillingTermsConsent ||
          (newPaymentModal && paymentPopup.showPrivacy)) && (
          <div className={classes.policy} style={{display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '20px', paddingBottom: '20px'}}>
            {showBillingTermsConsent ? (
              <BillingConsent
                ref={consentRef}
                checked={isConsentAccepted}
                onChange={handleConsentChange}
                error={isConsentError}
                onTermsClick={handleTermsClick}
                onBillingTermsClick={handleBillingTermsClick}
                // The checkbox label already states the consent — the legal line
                // under it said the same thing twice. It belongs to the variant
                // without the checkbox (the `else` below).
                showLegal={false}
              />
            ) : (
              <BillingTermsLegal
                onTermsClick={handleTermsClick}
                onBillingTermsClick={handleBillingTermsClick}
              />
            )}

            {/* Subscription terms — renewal date, "then $X / month", cancellation.
                Driven by showBillingTermsConsent: with the consent checkbox the
                modal shows the checkbox alone, without it this text is what the
                user reads at the bottom. Never shown for a one-time purchase
                (upsale offer), where nothing recurs. */}
            {newPaymentModal
              && paymentPopup.showPrivacy
              && !showBillingTermsConsent
              && !activeProduct.isOneTimePurchase && (
              <span className={classes.privacy}>
                {t("onboarding.personalPlan.plan.planExpires")} {plan_end_days}.{" "}
                {t("onboarding.personalPlan.plan.harged")} {subTitle}{" "}
                {t("onboarding.personalPlan.plan.for")} {titleProduct},{" "}
                {t("onboarding.personalPlan.plan.then")} {priceProduct}.{" "}
                {t("onboarding.personalPlan.plan.last")}
              </span>
            )}
          </div>
        )}
        {!activeProduct.isOneTimePurchase && (
          <>
            {isShowSafeCheckout ? <PaymentSafe /> : null}
            {!showTermsAtPlans && (
              <PaymentDescription activeProduct={activeProduct} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BasePaymentModalBody;
